import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { Bot, X, Send, Loader2, MessageSquare, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AIChatWidget = () => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Check if AI is enabled
    axios.get(`${API}/ai/settings`).then(res => {
      setAiEnabled(res.data?.ai_enabled === 'true');
    }).catch(() => setAiEnabled(false));
  }, [isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        conversation_id: conversationId,
      });
      setConversationId(res.data.conversation_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        tools_used: res.data.tools_used,
      }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to get AI response';
      setMessages(prev => [...prev, {
        role: 'error',
        content: detail,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  if (!isAuthenticated || !aiEnabled) return null;

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-[#1e3a8a] to-[#10b981] hover:from-[#1e40af] hover:to-[#059669] text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
          title="AI Assistant"
        >
          <Bot className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden transition-all duration-200 ${
          expanded
            ? 'bottom-4 right-4 w-[700px] h-[80vh]'
            : 'bottom-6 right-6 w-[400px] h-[560px]'
        }`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1e3a8a] to-[#10b981] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-white" />
              <span className="text-white font-semibold text-sm">AGI Production Assistant</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={startNewChat}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                title="New conversation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                title={expanded ? "Minimize" : "Expand"}
              >
                {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-10 h-10 text-blue-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-2">How can I help you today?</p>
                <div className="space-y-1.5">
                  {[
                    "Which orders are behind schedule?",
                    "Show me low stock items",
                    "What should we work on today?",
                    "Show the top sales order by price",
                    "What's in the Production stage?",
                    "Give me a summary of active quotes",
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => { setInput(suggestion); inputRef.current?.focus(); }}
                      className="block w-full text-left text-xs px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-[#1e3a8a] to-[#10b981] text-white'
                      : msg.role === 'error'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-slate-100 text-slate-800'
                  }`}
                >
                  <div className="whitespace-pre-wrap">
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          table: ({node, ...props}) => <table className="text-xs border-collapse w-full my-1" {...props} />,
                          th: ({node, ...props}) => <th className="border border-slate-300 px-1.5 py-0.5 bg-slate-50 text-left font-medium" {...props} />,
                          td: ({node, ...props}) => <td className="border border-slate-300 px-1.5 py-0.5" {...props} />,
                          p: ({node, ...props}) => <p className="mb-1 last:mb-0" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-semibold" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc pl-4 mb-1" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal pl-4 mb-1" {...props} />,
                          li: ({node, ...props}) => <li className="mb-0.5" {...props} />,
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    ) : msg.content}
                  </div>
                  {msg.tools_used && msg.tools_used.length > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t border-slate-200">
                      <span className="text-[10px] text-slate-400">
                        Used: {msg.tools_used.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-xl px-3 py-2 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-sm text-slate-500">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about orders, inventory, production..."
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="p-2 bg-gradient-to-r from-[#1e3a8a] to-[#10b981] text-white rounded-lg hover:from-[#1e40af] hover:to-[#059669] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
